import React, { useState } from 'react';
import { withTheme } from 'styled-components';
import PropTypes from 'prop-types';
import { additionalAlertTroubleshootingContext } from '@kentik/ui-shared/kaizen/constants';
import { AiContext, Button } from 'core/components';
import MitigateAlertButton from 'app/views/alerting/components/MitigateAlertButton';
import { AckAlertButton } from 'app/views/alerting/components/AckAlertButton';
import { UnackAlertButton } from 'app/views/alerting/components/UnackAlertButton';
import SilenceAlertButton from 'app/views/alerting/components/SilenceAlertButton';
import SuppressAlertButton from 'app/views/alerting/components/SuppressAlertButton';
import ClearAlarmButton from 'app/views/alerting/components/ClearAlarmButton';
import DashboardLink from 'app/components/links/DashboardLink';
import PolicyLink from 'app/components/links/PolicyLink';
import { AiFillBug } from 'react-icons/ai';
import AlertDebugDialog from 'app/views/alerting/components/AlertDebugDialog';
import { PiSparkleFill } from 'react-icons/pi';
import { inject } from 'mobx-react';
import storeLoader from 'app/stores/storeLoader';

/**
 * This component displays various actions that can be take on an alert:
 * - Link to its detail page
 * - Ack/unack
 * - Clear
 * - Silence
 * - Suppress
 * - Mitigate
 * - Dashboard link
 * - Policy link
 * - Debug
 */
const AlertActions = ({
  alertModel = {},
  isSubtenant,
  tenant,
  showDetailsLink,
  showPolicyLink,
  $journeys,
  $auth,
  $companySettings,
  $runbook
}) => {
  const [showDebugDialog, setShowDebugDialog] = useState(false);
  const { policy } = alertModel;
  const dashboardId = policy?.get('dashboardID');

  const debugAlertButton = React.useRef(null);

  const hasAi = !!$companySettings.enableKentikAi;
  const hasJourneysV2 = $auth.hasPermission('kaizen', { overrideForSudo: false });

  const toggleDebugDialogVisibility = () => {
    setShowDebugDialog(!showDebugDialog);
    if (showDebugDialog && debugAlertButton.current.buttonRef) {
      debugAlertButton.current.buttonRef.focus();
    }
  };

  const getRunbookContext = () => {
    const runbooksFromRuleId = $runbook.collection.getRunbooksForResource('alert_policy', policy.ruleId);
    const runbooks =
      runbooksFromRuleId.length > 0
        ? runbooksFromRuleId
        : $runbook.collection.getRunbooksForResource('alert_policy', policy.id);

    return runbooks.length
      ? `Here ${runbooks.length === 1 ? 'is the runbook that is' : 'are the runbooks that are'} provided by the user as a way to troubleshoot this Alert:\n\n
        ${runbooks.map((runbook) => `--- ${runbook.toString()}`).join('\n\n')}`
      : '';
  };

  const alertContextAndSuggestions = () => {
    const fullAlertDetails = alertModel.get();
    const runbookContext = getRunbookContext();

    delete fullAlertDetails.policy; // avoid circular reference in the context

    return {
      model: fullAlertDetails,
      name: `Alert Details: ${policy.name}`,
      placeholder: 'Ask anything about this alert',
      context: `Alert details for ${policy.name}\n
        Description: ${policy.description}\n
        Policy Dimensions: ${policy.dimensions.join(', ')}\n
        Policy Filters: ${JSON.stringify(policy.filters)}\n
        Policy Selected Devices: ${JSON.stringify(policy.devices)}\n
        Policy Primary Metric: ${policy.metrics.join(', ')}\n
        Dimensions: ${JSON.stringify(alertModel.dimensions, null, 2)}\n
        Full alert details: ${JSON.stringify(fullAlertDetails, null, 2)}\n
        ${runbookContext || additionalAlertTroubleshootingContext}`,
      suggestions: ['Troubleshoot this alert for me'],
      type: 'alert'
    };
  };

  return (
    <>
      {showDetailsLink && (
        <Button
          textAlign="left"
          icon="zoom-in"
          rightIcon="arrow-right"
          fill
          onClick={() => window.open(alertModel.alertDetailLink, '_blank')}
        >
          View Alert Details
        </Button>
      )}
      {!isSubtenant && hasAi && hasJourneysV2 && (
        <Button
          icon={PiSparkleFill}
          textAlign="left"
          onClick={() => {
            $journeys.openCopilot({ isHalfScreen: true });
            $journeys.sendMessage('Troubleshoot this alert for me', { createNewJourney: true });
          }}
        >
          Investigate with AI Advisor
        </Button>
      )}
      {!isSubtenant && policy && showPolicyLink && (
        <PolicyLink
          id={policy?.get('policy_id') || policy?.id}
          as="Button"
          fill
          icon="edit"
          alignText="left"
          rightIcon="arrow-right"
        >
          {tenant ? 'Edit Parent Policy' : 'Edit Policy'}
        </PolicyLink>
      )}

      {!isSubtenant && !alertModel.isAcked && <AckAlertButton model={alertModel} />}
      {!isSubtenant && alertModel.isAcked && <UnackAlertButton model={alertModel} />}
      {!isSubtenant && alertModel.isActive && <ClearAlarmButton models={alertModel} />}
      {!isSubtenant && <SilenceAlertButton alertModel={alertModel} buttonProps={{ fill: true }} />}
      {!isSubtenant && <SuppressAlertButton alertModel={alertModel} buttonProps={{ fill: true }} />}
      {!isSubtenant && alertModel.canMitigate && (
        <MitigateAlertButton alertModel={alertModel} tenant={tenant} buttonProps={{ fill: true }} />
      )}

      {dashboardId && !alertModel.isMetricPolicy && (
        <DashboardLink
          as="Button"
          fill
          withIcon
          rightIcon="arrow-right"
          id={dashboardId}
          query={alertModel.dashboardQuery}
          alignText="left"
        >
          View Dashboard
        </DashboardLink>
      )}

      {!isSubtenant && (
        <Button
          fill
          textAlign="left"
          onClick={toggleDebugDialogVisibility}
          onSpaceOrEnter={toggleDebugDialogVisibility}
          icon={AiFillBug}
          ariaExpanded={showDebugDialog}
          ref={debugAlertButton}
        >
          Debug Alert&hellip;
        </Button>
      )}

      {!!showDebugDialog && (
        <AlertDebugDialog isOpen={showDebugDialog} alertModel={alertModel} onClose={toggleDebugDialogVisibility} />
      )}

      {!isSubtenant && <AiContext type="alert" {...alertContextAndSuggestions()} />}
    </>
  );
};

AlertActions.propTypes = {
  // This must be an AlertModel instance
  alertModel: PropTypes.object.isRequired,
  isSubtenant: PropTypes.bool,
  // TenantModel
  tenant: PropTypes.object,
  showDetailsLink: PropTypes.bool,
  showPolicyLink: PropTypes.bool
};

AlertActions.defaultProps = {
  isSubtenant: undefined,
  tenant: undefined,
  showDetailsLink: undefined,
  showPolicyLink: undefined
};

export default inject('$journeys', '$auth', '$companySettings')(storeLoader('$runbook')(withTheme(AlertActions)));
